import "./SpaceCTA.scss";
import classNames from "classnames";
import React, { useState } from "react";
import Measure, { ContentRect } from "react-measure";
import {
  AvatarGroupUser,
  UserAvatarGroup,
} from "App/common/UserAvatar/UserAvatarGroup";
import { USER_AVATAR_SIZE } from "App/common/UserAvatar/UserAvatar";

// The minimum gap kept between the status and facepile before the row stacks.
// A hidden "shadow" copy of the row lays out status + a flexible spacer +
// facepile on one line; we measure the spacer, and once it's squeezed below
// this, the real row switches to a centered vertical stack.
const MIN_GAP = 20;

/**
 * A status pill: an uppercase `label` plus a colored `dot`. Owns the shared
 * status styling (mono font, size, dot) so every status looks the same; callers
 * just pass the text and dot color. Green matches the SpaceNavigation share
 * indicator's "on" state, red its "off" state.
 *
 * Pass it into `SpaceCTA`'s `status` slot, e.g.
 * `status={<SpaceStatus label="LIVE" dot="green" />}`. For the ticking
 * countdown, pass `<CountdownTimer />` into the same slot instead.
 */
export const SpaceStatus = ({
  label,
  dot,
}: {
  label: string;
  dot: "green" | "red";
}) => (
  <div className="space-cta-status">
    {label}
    <span className="space-cta-status-dot" data-dot={dot} />
  </div>
);

/**
 * The strictly-presentational space CTA lockup: a top row (a `status` node —
 * a status pill or the countdown timer — plus an optional member facepile that
 * wraps to a centered stack when it overflows) and a full-width `cta` beneath.
 *
 * It holds no logic: no membership, no link state, no viewport. Callers build
 * the `status` and `cta` nodes and pass them in. SpaceCountdownPanel is the rich
 * wrapper for the space page / join preview; the app-prompt dialog uses this
 * directly with a static "APP REQUIRED" status and a "Download the App" button.
 */
export const SpaceCTA = ({
  status,
  members,
  memberCount,
  cta,
  className,
}: {
  /** Top-left node: a status pill (`SpaceStatus`) or the countdown timer. */
  status: React.ReactNode;
  members?: AvatarGroupUser[];
  memberCount?: number;
  /** Full-width action beneath the row. */
  cta?: React.ReactNode;
  className?: string;
}) => {
  // Any member at all earns a facepile — matches the space page's top-right
  // cluster, which reads the same member set and has no minimum. Only an empty
  // space renders none.
  const showFacepile = (memberCount ?? 0) > 0;

  // Stack once the shadow row's spacer is squeezed below the minimum gap.
  const [spacerWidth, setSpacerWidth] = useState<number | null>(null);
  const isStacked =
    showFacepile && spacerWidth !== null && spacerWidth < MIN_GAP;

  const facepile = (
    <UserAvatarGroup
      users={members}
      total={memberCount}
      size={USER_AVATAR_SIZE.XLARGE}
    />
  );

  return (
    <div className={classNames("space-cta", className)}>
      {/* Shadow row: a hidden, non-wrapping copy at the real row's width. Its
          spacer flexes down as the pane narrows; we watch its width to decide
          when the real row can no longer keep the min gap and must stack. Same
          width as the real row, so no separate pane measurement is needed. */}
      {showFacepile ? (
        <div className="space-cta-row space-cta-row--shadow" aria-hidden>
          {status}
          <Measure
            bounds
            onResize={(rect: ContentRect) => {
              if (rect.bounds) setSpacerWidth(rect.bounds.width);
            }}
          >
            {({ measureRef }) => (
              <span ref={measureRef} className="space-cta-shadow-spacer" />
            )}
          </Measure>
          {facepile}
        </div>
      ) : null}

      <div className="space-cta-row" data-stacked={isStacked}>
        {status}
        {showFacepile ? facepile : null}
      </div>
      {cta}
    </div>
  );
};
